// /web/src/pages/projects/NewProject.jsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApi } from "../../lib/api";

import "../../styles/calculator.css";

export default function NewProject() {
  const api = useApi();
  const navigate = useNavigate();

  const [form, setForm] = React.useState({
    name: "",
    postcode: "",
    local_authority: "",
    building_use: "office",
    occupancy_ratio: "10",      // <-- default: 1:10 (10 m²/person)
    floors_count: "",
  });

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [lookupNote, setLookupNote] = React.useState("");

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function normalisePostcode(pc) {
    return (pc || "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/^UK/, "")
      .trim();
  }

  async function lookupLocalAuthority() {
    const raw = form.postcode;
    const pc = normalisePostcode(raw);
    if (!pc) return;

    try {
      const data = await api(`/geo/postcodes/${encodeURIComponent(pc)}`);
      const la = data?.local_authority || "";
      const postcode = data?.postcode || raw;
      setForm((f) => ({ ...f, postcode, local_authority: la }));
      setError("");
      setLookupNote(data?.lookup_note || "");
    } catch (err) {
      setLookupNote("");
      const message = err?.message || "";
      if (message.startsWith("Network error calling API")) {
        setError("Postcode lookup failed. Check your connection and try again.");
      } else if (message) {
        setError(message);
      } else {
        setError("Postcode lookup failed. Check your connection and try again.");
      }
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setLookupNote("");

    try {
      const body = {
        name: form.name.trim(),
        building_use: form.building_use || "office",
        postcode: form.postcode || null,
        local_authority: form.local_authority || null,
        occupancy_ratio:
          form.occupancy_ratio !== "" && form.occupancy_ratio !== null
            ? Number(form.occupancy_ratio)     // <-- numeric value (6,8,10,12.5,15)
            : null,
        floors_count:
          form.floors_count !== "" && form.floors_count !== null
            ? Number(form.floors_count)
            : null,
      };

      if (!body.name) throw new Error("Please enter a project name.");

      const created = await api("/projects", { method: "POST", body });
      navigate(`/projects/${created.id}/areas`);
    } catch (e) {
      setError(e?.message || "Failed to create project");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="pd-section pd-calculator pd-new-project">
      <div className="pd-container narrow">
        <div className="pd-calculator__header">
          <div>
            <p className="pd-eyebrow">Projects</p>
            <h1 className="pd-h2">Create a new project</h1>
            <p className="pd-muted">Add the core building details so calculators stay in sync.</p>
          </div>
          <div className="pd-actions">
            <Link to="/dashboard" className="pd-btn pd-btn--ghost">
              Back to dashboard
            </Link>
          </div>
        </div>

        {error && (
          <div className="pd-inline-alert pd-inline-alert--error">
            <strong>Error:</strong> {error}
          </div>
        )}
        {!error && lookupNote && (
          <div className="pd-inline-alert pd-inline-alert--info">
            <strong>Heads up:</strong> {lookupNote}
          </div>
        )}

        <form onSubmit={onSubmit} className="pd-panel">
          <div className="pd-panel__head">
            <div>
              <p className="pd-panel__title">Project information</p>
              <p className="pd-panel__meta">Postcode and occupancy values power every calculator.</p>
            </div>
          </div>

          <label className="pd-form-group pd-form-group--full">
            <span>Project name</span>
            <input
              required
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="e.g. 105 Old Lane"
            />
          </label>

          <div className="pd-input-grid" style={{ gridTemplateColumns: "minmax(0, 2fr) auto" }}>
            <label className="pd-form-group">
              <span>Postcode</span>
              <input
                name="postcode"
                value={form.postcode}
                onChange={onChange}
                onBlur={lookupLocalAuthority}
                placeholder="SW1A 1AA"
                autoCapitalize="characters"
              />
            </label>
            <div className="pd-form-group" style={{ alignSelf: "end" }}>
              <span aria-hidden="true">&nbsp;</span>
              <button
                type="button"
                className="pd-btn pd-btn--ghost pd-btn--compact"
                onClick={lookupLocalAuthority}
              >
                Lookup
              </button>
            </div>
          </div>

          <label className="pd-form-group pd-form-group--full">
            <span>Local authority</span>
            <input
              name="local_authority"
              value={form.local_authority}
              onChange={onChange}
              placeholder="Auto-populated from postcode (editable)"
            />
          </label>

          <div className="pd-input-grid pd-input-grid--triple">
            <label className="pd-form-group">
              <span>Building use</span>
              <select name="building_use" value={form.building_use} onChange={onChange}>
                <option value="office">Office</option>
              </select>
            </label>

            <label className="pd-form-group">
              <span>Occupancy ratio</span>
              <select name="occupancy_ratio" value={form.occupancy_ratio} onChange={onChange}>
                <option value="6">1:6</option>
                <option value="8">1:8</option>
                <option value="10">1:10 (default)</option>
                <option value="12.5">1:12.5</option>
                <option value="15">1:15</option>
              </select>
              <p className="pd-form-note">Stored as m²/person (e.g. 1:10 → 10)</p>
            </label>

            <label className="pd-form-group">
              <span>Number of floors</span>
              <input
                type="number"
                min="1"
                step="1"
                name="floors_count"
                value={form.floors_count}
                onChange={onChange}
                placeholder="e.g. 10"
              />
              <p className="pd-form-note">Include ground and basement levels.</p>
            </label>
          </div>

          <div className="pd-panel__footer">
            <span className="pd-muted">You can edit these details later from the project page.</span>
            <div className="pd-panel__actions">
              <button className="pd-btn pd-btn--primary" disabled={submitting} type="submit">
                {submitting ? "Creating…" : "Next step: Input areas"}
              </button>
              <Link to="/dashboard" className="pd-btn pd-btn--ghost">
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
